// @flow
import { isString, isBlank } from "voca";
import { transform, first} from "lodash";
import appConfig from "appConfig";
import consts from "common/consts";
import * as i18n from "common/i18n";
import { isMobile, doLater } from "utils/domUtils";
import createBoundActionCreators, {
	createCustomCreator,
	createDispatcher,
} from "common/actionsBase";
import notifications, { NOTIFICATION_IDS } from "../data/NotificationsData";
import {
	selectWidgetViewType,
	selectConfigLanguage,
	selectAddedUploads,
	selectPreQueuePath,
	selectAutoMinimizeConfig,
	selectWidgetViewTypeChangeByUser,
	selectIsInlineMode,
	selectCloudName,
} from "../selectors";
import loadCustomImages from "./configImagesLoader";

type ViewTypeData = { type: number, userAction: boolean, router: Object };

const notificationTimeoutHandlers = {};
let registeredEscListener = false;

export const types = {
	SET_WIDGET_CONFIG: "SET_WIDGET_CONFIG",
	SET_WIDGET_STYLES: "SET_WIDGET_STYLES",
	SET_QUEUE_ITEMS: "SET_QUEUE_ITEMS",
	SET_QUEUE_ITEMS_RETRY: "SET_QUEUE_ITEMS_RETRY",
	SET_QUEUE_ITEMS_ABORT: "SET_QUEUE_ITEMS_ABORT",
	SET_ACTIVE_SOURCE: "SET_ACTIVE_SOURCE",
	SET_MAX_SELECT_REACHED: "SET_MAX_SELECT_REACHED",
	SET_INVALID_PUBLIC_ID_FOUND: "SET_INVALID_PUBLIC_ID_FOUND",
	SET_ERROR: "SET_ERROR",
	SET_PROMPT: "SET_PROMPT",
	SET_IS_RESPONSIVE: "SET_IS_RESPONSIVE",
	SET_UPLOAD_TAGS: "SET_UPLOAD_TAGS",
	SET_UPLOAD_PRESET: "SET_UPLOAD_PRESET",
	SET_PUBLIC_ID: "SET_PUBLIC_ID",
	SET_PUBLIC_ID_INVALID: "SET_PUBLIC_ID_INVALID",
	SET_WIDGET_SHOWING: "SET_WIDGET_SHOWING",
	SET_WIDGET_VIEW_TYPE: "SET_WIDGET_VIEW_TYPE",
	SET_SUGGESTED_TAGS: "SET_SUGGESTED_TAGS",
	SET_SUGGESTED_UPLOAD_PRESETS: "SET_SUGGESTED_UPLOAD_PRESETS",
	SET_TAGS_INPUT_VALUE: "SET_TAGS_INPUT_VALUE",
	SET_BATCH_STARTED: "SET_BATCH_STARTED",
	SET_IS_PREMIUM: "SET_IS_PREMIUM",
	SET_SUCCESSFUL_COUNTER: "SET_SUCCESSFUL_COUNTER",
	SET_CUSTOM_IMAGES_LOAD_RESULTS: "SET_CUSTOM_IMAGES_LOAD_RESULTS",
	SET_CUSTOM_IMAGES_TO_LOAD: "SET_CUSTOM_IMAGES_TO_LOAD",
	SET_DICTIONARY_TEXT_CHANGED: "SET_DICTIONARY_TEXT_CHANGED",
	SET_LAST_PRE_QUEUE_PATH: "SET_LAST_PRE_QUEUE_PATH",
	SET_UPLOADER_CROP_SOURCES: "SET_UPLOADER_CROP_SOURCES",
	SET_DEFAULT_CROP: "SET_DEFAULT_CROP",
	SET_CROP: "SET_CROP",
	SET_METADATA_SCHEMA_DATA: "SET_METADATA_SCHEMA_DATA",
	SET_METADATA_INFO: "SET_METADATA_INFO",
	ADD_UPLOAD_TO_QUEUE: "ADD_UPLOAD_TO_QUEUE",
	ADD_NOTIFICATION: "ADD_NOTIFICATION",
	DO_CLOSE_WIDGET: "DO_CLOSE_WIDGET",
	DO_INC_CLOSE_COUNTER: "DO_INC_CLOSE_COUNTER",
	DO_REGISTER_GLOBAL_KEY_EVENTS: "DO_REGISTER_GLOBAL_KEY_EVENTS",
	DO_NAV_TO_PREVIOUS_PATH: "DO_NAV_TO_PREVIOUS_PATH",
	TOGGLE_ADVANCED_OPTIONS: "TOGGLE_ADVANCED_OPTIONS_SHOW",
	SET_ADVANCED_OPTIONS: "SET_ADVANCED_OPTIONS_SHOW",
	TOGGLE_RESPONSIVE_MENU: "TOGGLE_RESPONSIVE_MENU",
	CLEAR_MAX_SELECT_REACHED: "CLEAR_MAX_SELECT_REACHED",
	CLEAR_NOTIFICATION: "CLEAR_NOTIFICATION",
	CLEAR_ALL_NOTIFICATIONS: "CLEAR_ALL_NOTIFICATIONS",
	CLEAR_QUEUE: "CLEAR_QUEUE",
	CLEAR_ADVANCED_VALUES: "CLEAR_ADVANCED_VALUES",
	CLEAR_CANCELLED_UPLOADS: "CLEAR_CANCELLED_UPLOADS",
	REMOVE_UPLOAD_TAG: "REMOVE_UPLOAD_TAG",
	REMOVE_LAST_UPLOAD_TAG: "REMOVE_LAST_UPLOAD_TAG",
	SET_TABS_ROUTES: "SET_TABS_ROUTES",
};

const clearNotificationTimeoutHandler = (id) => {
	if (notificationTimeoutHandlers[id]) {
		notificationTimeoutHandlers[id]();
		notificationTimeoutHandlers[id] = null;
	}
};

const addNotification = (data: { id: string, persist?: boolean }, dispatch: Function) => {
	createDispatcher(dispatch)(types.ADD_NOTIFICATION, { notification: data });

	clearNotificationTimeoutHandler(data.id);

	if (!data.persist) {
		notificationTimeoutHandlers[data.id] = doLater(() => // store handler for timeout to be cleared)
			removeNotification({ id: data.id }, dispatch), appConfig.notifications.timeout);
	}
};

const removeNotification = ({ id }, dispatch: Function) => {
	createDispatcher(dispatch)(types.CLEAR_NOTIFICATION, { id });
	clearNotificationTimeoutHandler(id);
};

// const clearNotificationTimeout = ({id}) => {
//     //todo: prevent timeout when user hovers over notification
// };

const URL_TEST_RGX = /^(https?:\/|data:image)\//i;

const isUrl = (str) => !isBlank(str) && URL_TEST_RGX.test(str);

const isValidQueueAddInfo = (data: Object) => (data.files && data.files.length &&
	!~data.files.findIndex((f) => (isString(f) && !isUrl(f)) ||
		(isString(f.file) && !isUrl(f.file))));

const getNewData = (data: { files: Object[], cropInfo: ?Object[]}, state: Object) => {
	const alreadyAdded = selectAddedUploads(state);
	const filteredData = {
		files: data.files,
		cropInfo: data.cropInfo ? data.cropInfo : []
	};

	if (alreadyAdded?.files?.length) {
		const alreadyCropped = first(data.cropInfo) && alreadyAdded.cropInfo;

		filteredData.cropInfo = transform(data.files, (acc, f, i) => {
			const cropInfo = data.cropInfo && data.cropInfo[i];
			const notAlreadyCropped = alreadyCropped && !alreadyAdded.cropInfo[alreadyAdded.files.indexOf(f)];

			if (cropInfo || notAlreadyCropped) {
				acc.push(cropInfo);
			}
		}, []);
	}

	return filteredData;
};

const dispatchClose = (dispatch: Function) => {
	createDispatcher(dispatch)([
		[types.DO_INC_CLOSE_COUNTER],
		[types.TOGGLE_RESPONSIVE_MENU, { show: true }],
	]);
};

const getWidgetViewTypeForUpload = (getState: Function): number => {
	const state = getState();
	const widgetViewTypeChangeByUser = selectWidgetViewTypeChangeByUser(state);
	const autoMinimize = selectAutoMinimizeConfig(state);
	const isInline = selectIsInlineMode(state);

	let viewType = selectWidgetViewType(state);

	if (viewType !== consts.WIDGET_VIEW_TYPES.MINI) {
		if ((autoMinimize && !widgetViewTypeChangeByUser && !isInline)) {
			viewType = consts.WIDGET_VIEW_TYPES.MINI;
		} else {
			viewType = consts.WIDGET_VIEW_TYPES.EXPANDED;
		}
	}

	return viewType;
};

const navigateToPreviousPath = (router, state) => {
	const preQueuePath = selectPreQueuePath(state);

	if (preQueuePath) {
		router.history.push(preQueuePath); // go to the tab before we entered upload flow (with crop)
	} else {
		router.history.goBack(); // go back to tab from queue view
	}
};

const registerEscListener = (dispatch: Function, getState: Function) => {
	if (!registeredEscListener) {
		const state = getState();
		const isInline = selectIsInlineMode(state);
		const cloudName = selectCloudName(state);

		if (cloudName && !isMobile() && !isInline) {
			self.addEventListener("keyup", (e) => {
				if (e.which === 27) {
					dispatchClose(dispatch);
				}
			});

			registeredEscListener = true;
		}
	}
};

export default createBoundActionCreators(types, {

	[types.ADD_UPLOAD_TO_QUEUE]: createCustomCreator((data: Object, dispatch: Function, getState: Function, dispatcher: Function) => {
		if (isValidQueueAddInfo(data)) {
			const newData = getNewData(data, getState());

			if (newData.files.length || newData.cropInfo.length) {
				dispatcher(types.ADD_UPLOAD_TO_QUEUE, {
						files: newData.files,
						cropInfo: newData.cropInfo,
				});
			} else {
				addNotification(notifications[NOTIFICATION_IDS.NO_NEW_FILES], dispatch);
			}
		} else {
			addNotification(notifications[NOTIFICATION_IDS.INVALID_URL], dispatch);
		}
	}),

	[types.SET_ERROR]: createCustomCreator((data: Object, dispatch: Function) => addNotification({
		...notifications[NOTIFICATION_IDS.ERROR],
		text: data.text,
	}, dispatch)),

	[types.SET_PROMPT]: createCustomCreator((data: Object, dispatch: Function) => addNotification({
		...notifications[NOTIFICATION_IDS.PROMPT],
		text: data.text,
		actions: data.actions,
		width: typeof data.width === "undefined" ? 50 : data.width,
		persist: typeof data.persist === "undefined" ? true : data.persist,
		underlay: typeof data.underlay === "undefined" ? true : data.underlay,
	}, dispatch)),

	[types.SET_INVALID_PUBLIC_ID_FOUND]: createCustomCreator((dispatch: Function, getState: Function, dispatcher: Function) => {
		dispatcher(types.SET_INVALID_PUBLIC_ID_FOUND);
		addNotification(notifications[NOTIFICATION_IDS.INVALID_PUBLIC_ID], dispatch);
	}),

	[types.SET_MAX_SELECT_REACHED]: createCustomCreator((dispatch: Function) =>
		addNotification(notifications[NOTIFICATION_IDS.SELECTION_MAX_REACHED], dispatch)),

	[types.CLEAR_MAX_SELECT_REACHED]: createCustomCreator((dispatch: Function) =>
		removeNotification({ id: NOTIFICATION_IDS.SELECTION_MAX_REACHED }, dispatch)),

	[types.CLEAR_NOTIFICATION]: createCustomCreator((data, dispatch: Function) =>
		removeNotification(data, dispatch)),

	[types.DO_CLOSE_WIDGET]: createCustomCreator(dispatchClose),

	[types.DO_REGISTER_GLOBAL_KEY_EVENTS]: createCustomCreator((dispatch: Function, getState: Function) => {
		registerEscListener(dispatch, getState);
	}),

	[types.SET_WIDGET_VIEW_TYPE]: createCustomCreator((data: ViewTypeData, dispatch: Function, getState: Function, dispatcher: Function) => {
		if (data.router) {
			const state = getState();

			if (data.type === consts.WIDGET_VIEW_TYPES.INITIAL
				&& data.type !== selectWidgetViewType(state)) {
				navigateToPreviousPath(data.router, state);
			}
		}

		dispatcher(types.SET_WIDGET_VIEW_TYPE, { type: data.type, userAction: data.userAction });
	}),

	[types.SET_WIDGET_CONFIG]: createCustomCreator((config, dispatch: Function, getState: Function, dispatcher: Function) => {
		const state = getState();

		if (config.language && selectConfigLanguage(state) !== config.language) {
			i18n.setLanguage(config.language);
		}

		const dispatcherActions = [[types.SET_WIDGET_CONFIG, config]];

		if (config.text) { // not bothering to compare to state because will need deep compare
			i18n.addValues(config.text);
			dispatcherActions.push([types.SET_DICTIONARY_TEXT_CHANGED]);
		}

		if (config.styles || config.theme) {
			dispatcherActions.push([types.SET_WIDGET_STYLES, {
				theme: config.theme,
				...config.styles,
			}]);
		}

		dispatcher(dispatcherActions);

		loadCustomImages(config, dispatcher, types, getState);
	}),

	[types.CLEAR_ALL_NOTIFICATIONS]: createCustomCreator((dispatch: Function) => {
		Object.keys(notificationTimeoutHandlers)
			.forEach((id) => removeNotification({ id }, dispatch));
	}),

	[types.SET_BATCH_STARTED]: createCustomCreator((dispatch: Function, getState: Function, dispatcher: Function) => {
		const viewType = getWidgetViewTypeForUpload(getState);

		dispatcher([[
			types.SET_WIDGET_VIEW_TYPE,
			{ type: viewType },
		], [
			types.SET_BATCH_STARTED,
		]]);
	}),

	[types.DO_NAV_TO_PREVIOUS_PATH]: createCustomCreator((router: Object, dispatch: Function, getState: Function) =>
		navigateToPreviousPath(router, getState())),
});

export {
	addNotification,
};
